import jwtDecode from 'jwt-decode';
import cookie from 'js-cookie';
import AppConfig from './app-config.mjs';
import { fetchAuthSession } from 'aws-amplify/auth';

export async function fetchCognitoToken() {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  return idToken;
}

export default async function getAccessTokenInstance() {
  const token = await fetchCognitoToken();
  if (!token) {
    throw new Error('No access token available');
  }
  return new AccessToken(token);
}

export class AccessToken {
  token: string;

  constructor(tokenId: string) {
    this.token = tokenId;
  }

  private getTokenClaims(): JwtToken {
    return jwtDecode(this.token) as JwtToken;
  }

  getStoreId() {
    return this.getTokenClaims()?.['custom:storeId'] || 
           (this.isGlobalAdminRole() ? cookie.get(AppConfig.cookieStoreIdName) : null);
  }

  getDealerId() {
    return this.getTokenClaims()?.['custom:dealerId'];
  }

  getGroup() {
    return this.getTokenClaims()?.['cognito:groups'];
  }

  isStoreAdminRole() {
    const groups: Array<string> | undefined | null = this.getGroup();
    return Boolean(groups && groups[0] === 'admin');
  }

  isStoreAgentRole() {
    return this.getGroup()?.includes('agent') || false
  }

  isGlobalAdminRole() {
    return this.getGroup()?.includes('globalAdmin') || false
  }
  isGlobalOperationsRole() {
    return this.getGroup()?.includes('globalOperations') || false
  }

  isGlobalAdmin(): boolean {
    return Boolean(this.isGlobalAdminRole() && !this.isStoreAdmin());
  }
  isGlobalOperationsAdmin(): boolean {
    return Boolean(this.isGlobalOperationsRole() && !this.isStoreAdmin());
  }
  getUserPermissions() {
    const tokenDetails = this.getTokenClaims();
    return tokenDetails && tokenDetails?.permission ? tokenDetails.permission : {};
  }
  getMyFFLClaimProfiles() {
    const tokenDetails = this.getTokenClaims();
    return tokenDetails && tokenDetails?.claimed_profiles && tokenDetails?.claimed_profiles.length > 0 ? tokenDetails.claimed_profiles : [];
  }
  getStoreDealerId() {
    const tokenDetails = this.getTokenClaims();
    return tokenDetails && tokenDetails?.dealer_id ? tokenDetails.dealer_id : {};
  }

  isStoreAdmin() {
    return Boolean((this.isStoreAdminRole() || this.isGlobalAdminRole()) && this.getStoreId());
  }

  isImpersonating() {
    return Boolean(this.isGlobalAdminRole() && this.isStoreAdmin());
  }

  isMyFFLRole() {
    return this.getGroup()?.includes('myffl_user') || false;
  }

  getFullName() {
    const tokenClaims = this.getTokenClaims();
    const firstName = tokenClaims?.['given_name'];
    const lastName = tokenClaims?.['family_name'];
    return firstName && lastName ? `${firstName} ${lastName}` : '';
  }

  getEmail() {
    return this.getTokenClaims()?.['email'];
  }

  getUserId() {
    return this.getTokenClaims()?.['cognito:username'];
  }
  isPreferredReportsRole() {
    return this.getGroup()?.includes('preferredReports') || false;
  }

  isSearchOnlyRole() {
    return this.getGroup()?.includes('searchOnly') || false;
  }
}

// Type definition for JWT token structure
interface JwtToken {
  'custom:storeId'?: string;
  'custom:dealerId'?: string;
  'cognito:groups'?: string[];
  'given_name'?: string;
  'family_name'?: string;
  'email'?: string;
  'cognito:username'?: string;
  'permission'?: any;
  [key: string]: any;
}